import { motion } from 'framer-motion'
import { ExternalLink, Github, Calendar, User, CheckCircle, Clock } from 'lucide-react'
import { useState } from 'react'

const PortfolioCard = ({ project, onViewDetails, language }) => {
  const [isHovered, setIsHovered] = useState(false)
  
  const description = project.description[language] || project.description.sq
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'live': return 'bg-green-500/20 text-green-400 border-green-500/30'
      case 'completed': return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      case 'in-progress': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }
  }
  
  const getStatusIcon = (status) => {
    switch(status) {
      case 'live': return <CheckCircle className="w-3 h-3" />
      case 'completed': return <CheckCircle className="w-3 h-3" />
      case 'in-progress': return <Clock className="w-3 h-3" />
      default: return null
    }
  }
  
  const getStatusLabel = (status) => {
    const labels = {
      live: { sq: 'Live', en: 'Live' },
      completed: { sq: 'E Kompletuar', en: 'Completed' },
      'in-progress': { sq: 'Në Progres', en: 'In Progress' }
    }
    return labels[status]?.[language] || status
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -8, scale: 1.02 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="group relative bg-gradient-to-br from-bronze-900/90 to-bronze-950/90 backdrop-blur-xl rounded-3xl overflow-hidden border border-gold-400/20 hover:border-gold-400/50 transition-all duration-500 hover:shadow-2xl hover:shadow-gold-400/30 cursor-pointer"
      onClick={onViewDetails}
    >
      {/* Featured Badge */}
      {project.featured && (
        <div className="absolute top-4 left-4 z-20 bg-gradient-gold px-3 py-1.5 rounded-full text-xs font-bold text-white shadow-lg">
          ⭐ Featured
        </div>
      )}
      
      {/* Status Badge */}
      <div className={`absolute top-4 right-4 z-20 px-3 py-1.5 rounded-full text-xs font-semibold border flex items-center gap-1.5 backdrop-blur-xl ${getStatusColor(project.status)}`}>
        {getStatusIcon(project.status)}
        {getStatusLabel(project.status)}
      </div>
      
      {/* Thumbnail/Preview */}
      <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-bronze-800 to-dark group-hover:ring-2 group-hover:ring-gold-400/50 transition-all duration-300">
        {project.thumbnail ? (
          <>
            <motion.img
              src={project.thumbnail}
              alt={project.title}
              className="w-full h-full object-cover"
              animate={{ scale: isHovered ? 1.15 : 1 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            />
            {/* Shine effect on hover */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: isHovered ? '200%' : '-100%' }}
              transition={{ duration: 0.8 }}
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-6xl font-display text-gold-400/30">
            {project.title.charAt(0)}
          </div>
        )}
        
        {/* Overlay on Hover */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          className="absolute inset-0 bg-gradient-to-t from-dark via-dark/80 to-transparent flex items-center justify-center gap-3"
        >
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={onViewDetails}
            className="px-5 py-2.5 bg-gradient-gold text-white rounded-full font-semibold text-sm shadow-lg hover:shadow-gold-400/50 transition-shadow flex items-center gap-2"
          >
            <span>{language === 'sq' ? 'Shiko Detajet' : 'View Details'}</span>
            <ExternalLink className="w-4 h-4" />
          </motion.button>
          
          {project.liveUrl && (
            <motion.a
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="p-2.5 bg-white/10 backdrop-blur-xl text-white rounded-full hover:bg-white/20 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-5 h-5" />
            </motion.a>
          )}
          
          {project.githubUrl && (
            <motion.a
              href={project.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="p-2.5 bg-white/10 backdrop-blur-xl text-white rounded-full hover:bg-white/20 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Github className="w-5 h-5" />
            </motion.a>
          )}
        </motion.div>
        
        {/* Gradient Overlay at Bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-bronze-900/80 to-transparent" />
      </div>
      
      {/* Content */}
      <div className="p-6">
        {/* Category & Year */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold text-gold-400 uppercase tracking-wider">
            {project.type}
          </span>
          <div className="flex items-center gap-1.5 text-xs text-beige-300">
            <Calendar className="w-3.5 h-3.5" />
            {project.year}
          </div>
        </div>
        
        {/* Title */}
        <h3 className="text-xl font-display font-bold text-cream mb-2 group-hover:text-gold-400 transition-colors line-clamp-1">
          {project.title}
        </h3>
        
        {/* Client */}
        {project.client && (
          <div className="flex items-center gap-1.5 text-sm text-beige-300/80 mb-3">
            <User className="w-3.5 h-3.5" />
            <span>{project.client}</span>
          </div>
        )}
        
        {/* Description */}
        <p className="text-sm text-beige-200/80 leading-relaxed mb-4 line-clamp-2">
          {description}
        </p>
        
        {/* Technologies */}
        <div className="flex flex-wrap gap-2 mb-4">
          {project.technologies.slice(0, 4).map((tech, index) => (
            <span
              key={index}
              className="px-2.5 py-1 bg-gold-400/10 text-gold-400 text-xs font-medium rounded-full border border-gold-400/20"
            >
              {tech}
            </span>
          ))}
          {project.technologies.length > 4 && (
            <span className="px-2.5 py-1 bg-gold-400/5 text-gold-400/60 text-xs font-medium rounded-full border border-gold-400/10">
              +{project.technologies.length - 4}
            </span>
          )}
        </div>
        
        {/* Color Palette */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-beige-300/60">Colors:</span>
          <div className="flex gap-1.5">
            {project.colors.slice(0, 5).map((color, index) => (
              <div
                key={index}
                className="w-5 h-5 rounded-full border-2 border-white/20 shadow-sm"
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default PortfolioCard
